import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Users, PlusCircle, Moon, Sun, LogOut, BarChart3 } from 'lucide-react';

const DashboardDocente = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/reportes/dashboard-docente`,
          { withCredentials: true }
        );
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard Docente</h1>
            <p className="text-sm text-muted-foreground">Bienvenido, {user?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-button"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {loading ? (
            <>
              <Card className="animate-pulse"><CardHeader><div className="h-4 bg-muted rounded w-1/2 mb-2"></div><div className="h-8 bg-muted rounded w-1/3"></div></CardHeader></Card>
              <Card className="animate-pulse"><CardHeader><div className="h-4 bg-muted rounded w-1/2 mb-2"></div><div className="h-8 bg-muted rounded w-1/3"></div></CardHeader></Card>
            </>
          ) : (
            <>
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardDescription className="text-xs uppercase tracking-wider">Mis Actividades</CardDescription>
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-3xl font-bold">{stats?.mis_actividades || 0}</CardTitle>
                </CardHeader>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardDescription className="text-xs uppercase tracking-wider">Mis Grupos</CardDescription>
                    <Users className="h-5 w-5 text-secondary" />
                  </div>
                  <CardTitle className="text-3xl font-bold">{stats?.mis_grupos || 0}</CardTitle>
                </CardHeader>
              </Card>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-border hover:-translate-y-1"
            onClick={() => navigate('/docente/actividades/nueva')}
            data-testid="nav-crear-actividad-button"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <PlusCircle className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Nueva Actividad</CardTitle>
                  <CardDescription>Crear actividad didáctica</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-border hover:-translate-y-1"
            onClick={() => navigate('/docente/actividades')}
            data-testid="nav-mis-actividades-button"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-secondary/10">
                  <BookOpen className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Mis Actividades</CardTitle>
                  <CardDescription>Ver y gestionar</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all border-border hover:-translate-y-1"
            onClick={() => navigate('/docente/resultados')}
            data-testid="nav-resultados-button"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-[hsl(var(--accent-green))]/10">
                  <BarChart3 className="h-6 w-6" style={{ color: 'hsl(var(--accent-green))' }} />
                </div>
                <div>
                  <CardTitle className="text-lg">Resultados</CardTitle>
                  <CardDescription>Ver progreso estudiantes</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DashboardDocente;
